import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiKey, FiPlus, FiSearch, FiCopy, FiRefreshCw, FiLoader, FiPauseCircle, FiPlayCircle, FiShield, FiX, FiCheck, FiChevronRight, FiCalendar, FiGlobe, FiInfo, FiActivity, FiBox, FiUsers, FiAlertTriangle, FiEdit2, FiSave } = FiIcons;

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, loading }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl p-6 text-center animate-in zoom-in-95 duration-200">
        <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiAlertTriangle} className="text-3xl" />
        </div>
        <h3 className="text-lg font-bold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-500 mt-2">{message}</p>
        <div className="flex gap-3 mt-6">
          <button onClick={onCancel} className="flex-1 py-2.5 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="flex-1 py-2.5 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-200">
            {loading && <SafeIcon icon={FiLoader} className="animate-spin" />} Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const APIKeyDrawer = ({ apiKey, clients, apps, onClose, onRefresh, onReplace }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  // PB-14B: State for Access & Limits editing
  const [isEditingAccessLimits, setIsEditingAccessLimits] = useState(false);
  const [savingAccessLimits, setSavingAccessLimits] = useState(false);
  const [editData, setEditData] = useState({
    key_name: '',
    monthly_limit: 0,
    annual_limit: 0,
    allowed_origins: '',
    from_name: '',
    from_email: '',
    reply_to_email: '',
    valid_from: '',
    valid_until: '',
    included_email_limit: 0
  });

  useEffect(() => {
    if (apiKey) {
      setEditData({
        key_name: apiKey.key_name || apiKey.name || '',
        monthly_limit: apiKey.monthly_limit || 0,
        annual_limit: apiKey.annual_limit || 0,
        allowed_origins: apiKey.allowed_origins || '',
        from_name: apiKey.from_name || '',
        from_email: apiKey.from_email || '',
        reply_to_email: apiKey.reply_to_email || '',
        valid_from: apiKey.valid_from ? apiKey.valid_from.substring(0, 10) : '',
        valid_until: apiKey.valid_until ? apiKey.valid_until.substring(0, 10) : '',
        included_email_limit: apiKey.included_email_limit || 0
      });
    }
  }, [apiKey, isEditingAccessLimits]);

  if (!apiKey) return null;

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdating(true);
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_api_key_status', payload: { api_key_id: apiKey.id, status: newStatus } })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      showToast(`API Key ${newStatus === 'active' ? 'activated' : 'revoked'}`);
      onRefresh();
      onClose();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setIsUpdating(false);
      setShowConfirm(false);
    }
  };

  // PB-14B: Handle saving Access & Limits
  const handleSaveAccessLimits = async () => {
    setSavingAccessLimits(true);
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_api_key',
          payload: {
            id: apiKey.id,
            key_name: editData.key_name,
            monthly_limit: Number(editData.monthly_limit || 0),
            annual_limit: Number(editData.annual_limit || 0),
            allowed_origins: editData.allowed_origins || "",
            from_name: editData.from_name || "",
            from_email: editData.from_email || "",
            reply_to_email: editData.reply_to_email || "",
            valid_from: editData.valid_from || "",
            valid_until: editData.valid_until || "",
            included_email_limit: Number(editData.included_email_limit || 0)
          }
        })
      });
      const result = await response.json();
      if (!result.success) {
        showToast(result.message || result.errorCode, 'error');
      } else {
        showToast(result.message || 'API Key updated successfully');
        setIsEditingAccessLimits(false);
        onRefresh();
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingAccessLimits(false);
    }
  };

  // PB-14B: Preset logic
  const set3MonthPreset = () => {
    const today = new Date();
    const validFrom = today.toISOString().substring(0, 10);
    const future = new Date(today);
    future.setMonth(future.getMonth() + 3);
    const validUntil = future.toISOString().substring(0, 10);
    setEditData({
      ...editData,
      valid_from: validFrom,
      valid_until: validUntil,
      included_email_limit: 2000
    });
  };

  const client = clients.find(c => c.id === apiKey.client_id);
  const app = apps.find(a => a.id === apiKey.app_id);
  const isActive = apiKey.status === 'active' || apiKey.status === 'enabled';

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="absolute inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><SafeIcon icon={FiInfo} /> API Key Intelligence</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors"><SafeIcon icon={FiX} /></button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
          {/* Header Info */}
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl ${isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
              <SafeIcon icon={FiKey} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-slate-900 truncate">{apiKey.key_name || apiKey.name}</h2>
              <p className="text-xs text-slate-400 font-mono flex items-center gap-1.5 uppercase">
                Prefix: {apiKey.key_prefix} <span className="opacity-30">••••••••</span>
              </p>
              <div className="mt-1 flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {apiKey.status}
                </span>
                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border ${apiKey.environment === 'live' ? 'bg-orange-50 text-orange-700 border-orange-100' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
                  {apiKey.environment}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-start gap-3">
            <SafeIcon icon={FiShield} className="text-amber-600 mt-0.5" />
            <p className="text-[10px] text-amber-800 leading-relaxed font-bold uppercase tracking-tight">
              Security Note: Full API keys are only shown once when generated. Generate a replacement key if you need a new full key.
            </p>
          </div>

          {/* Context Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5"><SafeIcon icon={FiUsers} /> Client</p>
              <p className="text-xs font-bold text-slate-700 truncate">{client ? (client.client_name || client.name) : 'N/A'}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1.5"><SafeIcon icon={FiBox} /> Application</p>
              <p className="text-xs font-bold text-slate-700 truncate">{app ? (app.app_name || app.name) : 'Standalone'}</p>
            </div>
          </div>

          {/* PB-14B: Access & Limits Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <SafeIcon icon={FiActivity} /> Access & Limits
              </h4>
              {!isEditingAccessLimits && (
                <button
                  onClick={() => setIsEditingAccessLimits(true)}
                  className="px-3 py-1 bg-white border border-slate-200 text-slate-600 text-[10px] font-bold rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1.5 shadow-sm"
                >
                  <SafeIcon icon={FiEdit2} /> Edit Access & Limits
                </button>
              )}
            </div>

            {isEditingAccessLimits ? (
              <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl space-y-5 animate-in fade-in slide-in-from-top-2">
                <div className="flex justify-between items-start gap-4">
                  <p className="text-[10px] text-slate-500 italic leading-tight">
                    Use Valid Until to extend or end a client’s included service period.
                  </p>
                  <button
                    onClick={set3MonthPreset}
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-[9px] font-black uppercase rounded hover:bg-blue-200 transition-colors shrink-0"
                  >
                    Set 3-Month Included Period
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase">Key Name</label>
                    <input type="text" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500" value={editData.key_name} onChange={e => setEditData({...editData, key_name: e.target.value})} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase">Monthly Limit</label>
                      <input type="number" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editData.monthly_limit} onChange={e => setEditData({...editData, monthly_limit: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase">Annual Limit</label>
                      <input type="number" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editData.annual_limit} onChange={e => setEditData({...editData, annual_limit: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase">Valid From</label>
                      <input type="date" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editData.valid_from} onChange={e => setEditData({...editData, valid_from: e.target.value})} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase">Valid Until</label>
                      <input type="date" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editData.valid_until} onChange={e => setEditData({...editData, valid_until: e.target.value})} />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase">Included Email Allowance</label>
                      <input type="number" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editData.included_email_limit} onChange={e => setEditData({...editData, included_email_limit: e.target.value})} />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase">Allowed Origins</label>
                      <input type="text" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-mono" placeholder="https://*" value={editData.allowed_origins} onChange={e => setEditData({...editData, allowed_origins: e.target.value})} />
                    </div>
                  </div>

                  <div className="pt-2 space-y-3">
                    <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-200 pb-1">Sender Identity Defaults</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase">From Name</label>
                        <input type="text" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editData.from_name} onChange={e => setEditData({...editData, from_name: e.target.value})} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase">From Email</label>
                        <input type="email" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editData.from_email} onChange={e => setEditData({...editData, from_email: e.target.value})} />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase">Reply-To Email</label>
                        <input type="email" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm" value={editData.reply_to_email} onChange={e => setEditData({...editData, reply_to_email: e.target.value})} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                  <button onClick={() => setIsEditingAccessLimits(false)} className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                  <button
                    onClick={handleSaveAccessLimits}
                    disabled={savingAccessLimits}
                    className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {savingAccessLimits ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />} Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Monthly Limit</p>
                  <p className="text-sm font-bold text-slate-700">{(apiKey.monthly_limit || 0).toLocaleString()} / mo</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Annual Limit</p>
                  <p className="text-sm font-bold text-slate-700">{(apiKey.annual_limit || 0).toLocaleString()} / yr</p>
                </div>
                <div className="col-span-2 p-4 bg-blue-50/50 rounded-2xl border border-blue-100">
                  <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Included Email Allowance</p>
                  <p className="text-sm font-black text-blue-700">
                    {apiKey.included_email_limit ? `${apiKey.included_email_limit.toLocaleString()} emails` : 'Not configured'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Valid From</p>
                  <p className="text-xs font-bold text-slate-700">{apiKey.valid_from ? new Date(apiKey.valid_from).toLocaleDateString() : 'Not configured'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Valid Until</p>
                  <p className="text-xs font-bold text-slate-700">{apiKey.valid_until ? new Date(apiKey.valid_until).toLocaleDateString() : 'Not configured'}</p>
                </div>
                <div className="col-span-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Allowed Origins</p>
                  <p className="text-[10px] font-mono text-slate-600 truncate">{apiKey.allowed_origins || 'All Origins (*)'}</p>
                </div>
                <div className="col-span-2 space-y-2">
                  <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mt-2">
                    <SafeIcon icon={FiShield} /> Sender Identity
                  </h5>
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-400">From Name:</span>
                      <span className="font-bold text-slate-700">{apiKey.from_name || 'System Default'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">From Email:</span>
                      <span className="font-bold text-slate-700">{apiKey.from_email || 'System Default'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Reply-To:</span>
                      <span className="font-bold text-slate-700">{apiKey.reply_to_email || 'Not configured'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-medium">
            <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} /> Issued {new Date(apiKey.created).toLocaleDateString()}</span>
            <span className="flex items-center gap-1"><SafeIcon icon={FiActivity} /> Last Used {apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleDateString() : 'Never'}</span>
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 bg-slate-50 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <button onClick={onClose} className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 text-sm font-bold rounded-xl hover:bg-slate-100 transition-all">Close</button>
            <button
              disabled={isEditingAccessLimits}
              onClick={() => onReplace({...apiKey, key_name: `Replacement for ${apiKey.key_name || apiKey.name}`})}
              className="px-4 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl shadow-md hover:bg-slate-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} /> Replace Key
            </button>
          </div>
          <button
            disabled={isUpdating || isEditingAccessLimits}
            onClick={() => isActive ? setShowConfirm(true) : handleStatusUpdate('active')}
            className={`w-full py-2.5 text-white text-sm font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${isActive ? 'bg-red-600 hover:bg-red-700 shadow-red-200' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'}`}
          >
            {isUpdating ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isActive ? FiPauseCircle : FiPlayCircle} />}
            {isActive ? 'Revoke Access Key' : 'Reactivate Access Key'}
          </button>
        </div>
      </div>
      <ConfirmationModal isOpen={showConfirm} title="Revoke API Access?" message={`This will immediately block all requests using prefix ${apiKey.key_prefix}. Are you sure?`} onConfirm={() => handleStatusUpdate('revoked')} onCancel={() => setShowConfirm(false)} loading={isUpdating} />
    </div>
  );
};

const GenerateKeyModal = ({ clients, apps, prefillData, onClose, onGenerated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(prefillData || {
    key_name: '',
    client_id: '',
    app_id: '',
    monthly_limit: 25000,
    annual_limit: 0,
    allowed_origins: '',
    from_name: '',
    from_email: '',
    reply_to_email: ''
  });

  useEffect(() => {
    if (prefillData) setFormData(prefillData);
  }, [prefillData]);

  const filteredApps = formData.client_id ? apps.filter(a => a.client_id === formData.client_id) : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.key_name) return showToast('Client and Key Name are required', 'error');
    setLoading(true);
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create_api_key', payload: formData })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Generation failed');
      onGenerated(result.data);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2"><SafeIcon icon={FiKey} className="text-blue-400" /> {prefillData ? 'Generate Replacement Key' : 'Generate API Key'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-800 rounded"><SafeIcon icon={FiX} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Key Name *</label>
              <input required type="text" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:bg-white focus:border-blue-500" placeholder="e.g. Production Website" value={formData.key_name} onChange={e => setFormData({...formData, key_name: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client *</label>
              <select required className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white" value={formData.client_id} onChange={e => setFormData({...formData, client_id: e.target.value, app_id: ''})}>
                <option value="">Select Client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name || c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Linked App (Optional)</label>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:bg-white" value={formData.app_id} onChange={e => setFormData({...formData, app_id: e.target.value})}>
                <option value="">Standalone / Unlinked</option>
                {filteredApps.map(a => <option key={a.id} value={a.id}>{a.app_name || a.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Monthly Limit</label>
              <input type="number" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.monthly_limit} onChange={e => setFormData({...formData, monthly_limit: parseInt(e.target.value)})} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Allowed Origins (Comma separated)</label>
            <input type="text" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono" placeholder="https://example.com, https://app.example.com" value={formData.allowed_origins} onChange={e => setFormData({...formData, allowed_origins: e.target.value})} />
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-4">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <SafeIcon icon={FiShield} /> Default Sender Identity (Optional)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-slate-500 uppercase">From Name</label>
                <input type="text" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs" value={formData.from_name} onChange={e => setFormData({...formData, from_name: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-slate-500 uppercase">From Email</label>
                <input type="email" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs" value={formData.from_email} onChange={e => setFormData({...formData, from_email: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-slate-500 uppercase">Reply-To</label>
                <input type="email" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs" value={formData.reply_to_email} onChange={e => setFormData({...formData, reply_to_email: e.target.value})} />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2 text-sm font-bold text-slate-500 hover:text-slate-700">Cancel</button>
            <button type="submit" disabled={loading} className="px-8 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 transition-all flex items-center gap-2 disabled:opacity-50">
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheck} />} Generate Key
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RevealKeyModal = ({ rawKey, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="p-8 space-y-6 text-center">
          <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto shadow-inner border border-blue-100">
            <SafeIcon icon={FiShield} className="text-4xl" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-800">Key Generated!</h3>
            <p className="text-sm text-slate-500 mt-2">This key will only be shown <b>once</b>. Please save it securely now.</p>
          </div>
          <div className="relative group">
            <div className="w-full px-4 py-4 bg-slate-900 text-emerald-400 font-mono text-xs rounded-xl border border-slate-800 break-all pr-12 text-left leading-relaxed">
              {rawKey}
            </div>
            <button onClick={handleCopy} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-all">
              <SafeIcon icon={copied ? FiCheck : FiCopy} className={copied ? 'text-emerald-400' : ''} />
            </button>
          </div>
          <div className="space-y-4 pt-2">
            <label className="flex items-center gap-3 justify-center cursor-pointer group">
              <input type="checkbox" className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
              <span className="text-xs font-bold text-slate-600 group-hover:text-slate-900">I have saved this key securely</span>
            </label>
            <button onClick={onClose} disabled={!confirmed} className="w-full py-3.5 bg-slate-900 text-white rounded-xl text-sm font-bold shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              Close & Finalize
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const APIKeys = () => {
  const [keys, setKeys] = useState([]);
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showGenModal, setShowGenModal] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [revealData, setRevealData] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const callGateway = async (action, payload = {}) => {
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, payload })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Gateway Error');
      return result.data;
    } catch (err) {
      throw err;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pbKeys, pbClients, pbApps] = await Promise.all([
        callGateway('list_api_keys'),
        callGateway('list_clients'),
        callGateway('list_apps')
      ]);
      setKeys(pbKeys || []);
      setClients(pbClients || []);
      setApps(pbApps || []);
      
      // Update selectedKey if it exists to refresh drawer content
      if (selectedKey) {
        const updated = (pbKeys || []).find(k => k.id === selectedKey.id);
        if (updated) setSelectedKey(updated);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (e, id, currentStatus) => {
    e.stopPropagation();
    setUpdatingId(id);
    const newStatus = (currentStatus === 'active' || currentStatus === 'enabled') ? 'revoked' : 'active';
    try {
      await callGateway('update_api_key_status', { api_key_id: id, status: newStatus });
      showToast(`API Key ${newStatus === 'active' ? 'activated' : 'revoked'}`);
      fetchData();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleKeyGenerated = (data) => {
    setShowGenModal(false);
    setRevealData(data);
    setPrefillData(null);
  };

  const handleRevealClose = () => {
    setRevealData(null);
    fetchData();
    showToast('API Key generation finalized');
  };

  const handleReplaceKey = (keyData) => {
    setPrefillData(keyData);
    setShowGenModal(true);
    setSelectedKey(null);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getClientName = (id) => {
    const c = clients.find(cl => cl.id === id);
    return c ? (c.client_name || c.name) : 'Unknown Client';
  };

  const filteredKeys = keys.filter(k => (k.key_name || k.name || '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">API Access Keys</h2>
          <p className="text-sm text-slate-500 mt-1">Manage secure application credentials.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 shadow-sm transition-all" title="Refresh list">
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => {setPrefillData(null); setShowGenModal(true);}} className="px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 shadow-md flex items-center gap-2">
            <SafeIcon icon={FiPlus} /> Generate Key
          </button>
        </div>
      </div>

      <div className="flex bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex-1 flex items-center px-3 gap-2">
          <SafeIcon icon={FiSearch} className="text-slate-400" />
          <input type="text" placeholder="Search keys..." className="w-full text-sm outline-none bg-transparent" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 relative min-h-[200px]">
        {loading && (
          <div className="absolute inset-0 bg-slate-50/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <SafeIcon icon={FiLoader} className="text-3xl text-blue-600 animate-spin" />
          </div>
        )}
        {filteredKeys.length === 0 && !loading && (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center text-slate-400 font-medium italic">
            No API keys found. Generate a new one to get started.
          </div>
        )}
        {filteredKeys.map(key => (
          <div
            key={key.id}
            className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:border-blue-200 transition-all overflow-hidden group cursor-pointer"
            onClick={() => setSelectedKey(key)}
          >
            <div className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-4 min-w-[250px]">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0 ${key.status === 'revoked' || key.status === 'suspended' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                  <SafeIcon icon={FiKey} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-800 truncate">{key.key_name || key.name}</h3>
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase ${key.environment === 'live' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                      {key.environment}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{getClientName(key.client_id)}</p>
                </div>
              </div>
              <div className="flex-1 bg-slate-50/50 rounded-xl p-3 border border-slate-100 flex items-center gap-4 font-mono text-[11px] text-slate-600">
                <span className="shrink-0">{key.key_prefix}</span>
                <span className="opacity-20 flex-1 truncate">••••••••••••••••••••••••••••••••</span>
                <span className={`ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${key.status === 'active' || key.status === 'enabled' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {key.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={(e) => handleUpdateStatus(e, key.id, key.status)} disabled={updatingId === key.id} className={`p-2 rounded-lg transition-colors ${key.status === 'active' || key.status === 'enabled' ? 'text-red-400 hover:bg-red-50 hover:text-red-600' : 'text-emerald-400 hover:bg-emerald-50 hover:text-emerald-600'}`} title={key.status === 'active' ? 'Revoke Key' : 'Activate Key'}>
                  <SafeIcon icon={updatingId === key.id ? FiLoader : (key.status === 'active' || key.status === 'enabled' ? FiPauseCircle : FiPlayCircle)} className={updatingId === key.id ? 'animate-spin' : ''} />
                </button>
                <button onClick={(e) => {e.stopPropagation(); setSelectedKey(key);}} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-blue-600 hover:text-white rounded-lg text-xs font-bold transition-all shadow-sm group-hover:bg-blue-500 group-hover:text-white">
                  Details <SafeIcon icon={FiChevronRight} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showGenModal && (
        <GenerateKeyModal clients={clients} apps={apps} prefillData={prefillData} onClose={() => setShowGenModal(false)} onGenerated={handleKeyGenerated} />
      )}
      {revealData && (
        <RevealKeyModal rawKey={revealData.api_key} onClose={handleRevealClose} />
      )}
      {selectedKey && (
        <APIKeyDrawer apiKey={selectedKey} clients={clients} apps={apps} onClose={() => setSelectedKey(null)} onRefresh={fetchData} onReplace={handleReplaceKey} />
      )}
    </div>
  );
};

export default APIKeys;